/* eslint-disable max-classes-per-file */
import {
    useEffect,
    useMemo,
} from "react";
import {
    DashboardGroupBy, DashboardSettings, DashboardSettingsFilters, DashboardSortBy, SearchSortBy,
} from "../../__generated__/api";
import { createDebounce } from "../../debouncer";
import { ShortcutApi } from "../KeyboardShortcutContext";
import useRouting, {
    PathValues, Paths, RoutingProps, useParams, useParamsAndPath,
} from "../Routing";
import { TicketStateContext } from "./TicketState";
import BaseListener from "./base-listener";
import TicketColumnGroup from "./ticket-group";

export interface SearchContext {
    isSearching: boolean;
    search?: string;
    sortBy?: SearchSortBy;
    shouldSearchArchived?: boolean;
    isChanged: boolean;
    group?: TicketColumnGroup;

    numericSearch?: number;

    isLoadingRemote: boolean;
    numberRemoteResults: number;

    filters?: DashboardSettingsFilters;
    groupBy?: DashboardGroupBy
}

const DEFAULT = {
    isSearching: false,
    sortBy: SearchSortBy.Relevance,
    shouldSearchArchived: true,
    isChanged: false,
    isLoadingRemote: false,
    numberRemoteResults: -1,
};

export const SORT_TO_DASHBOARD: Record<string, DashboardSortBy> = {
    [SearchSortBy.CreatedDateAscending]: DashboardSortBy.CreatedDateAscending,
    [SearchSortBy.CreatedDateDescending]: DashboardSortBy.CreatedDateDescending,
    [SearchSortBy.PriorityAscending]: DashboardSortBy.PriorityAscending,
    [SearchSortBy.PriorityDescending]: DashboardSortBy.PriorityDescending,
};

export const DASHBOARD_TO_SORT = Object.entries(SORT_TO_DASHBOARD)
    .toIdMap(([, value]) => value, ([key]) => key) as Record<string, SearchSortBy>;

export function removeEmptyKeys<T extends Record<string, unknown> | Record<string, unknown>[]>(obj: T): T {
    if (Array.isArray(obj)) {
        return obj.map(row => removeEmptyKeys(row)) as T;
    }

    if (obj == null || typeof obj !== "object" || obj instanceof Date) {
        return obj;
    }

    const copy: Record<keyof typeof obj, unknown> = Object.create(null);
    Object.entries(obj).forEach(([key, value]) => {
        if (value == null || value === "" || key === "__typename") {
            return;
        }
        const cleanValue = removeEmptyKeys(value as unknown as T);
        if (typeof cleanValue === "object" && Object.keys(cleanValue).isEmpty) {
            return;
        }
        copy[key as keyof typeof copy] = cleanValue;
    });
    return copy as T;
}

function serilizeFilters(filters: DashboardSettingsFilters) {
    const serialized = removeEmptyKeys(filters);
    return Object.keys(serialized).isNotEmpty ? JSON.stringify(serialized) : undefined;
}

export default class SearchState {
    public static useSearchState() {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const searchState = useMemo(() => new SearchState(), []);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const routing = useRouting();

        // eslint-disable-next-line react-hooks/rules-of-hooks
        searchState.useSearchContext();

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            const params = routing.getSearchParams(Paths.AGENT);
            if (window.location.pathname.startsWith(Paths.AGENT) && (params.search || params.filters)) {
                searchState.setSearchAndUpdate({
                    isSearching: true,
                    search: params.search,
                    sortBy: params.sortBy as SearchSortBy,
                    shouldSearchArchived: params.archived !== "false",
                    groupBy: params.groupBy as DashboardGroupBy,
                    filters: params.filters ? JSON.parse(params.filters) : undefined,
                });
            }
        }, []);

        searchState.setRouting(routing);

        return searchState;
    }

    private listener: BaseListener = new BaseListener();

    private search: SearchContext = DEFAULT;

    private lastSearch?: SearchContext;

    private searchDebouncer = createDebounce(500);

    private routing?: RoutingProps;

    private ticketState?: TicketStateContext;

    // eslint-disable-next-line no-useless-constructor
    private constructor() {
        // none
    }

    public setTicketState(ticketState: TicketStateContext) {
        this.ticketState = ticketState;
    }

    private setRouting(routing: RoutingProps) {
        this.routing = routing;
    }

    public useSearchContext() {
        this.listener.useListener();
        return this.search;
    }

    public setListener(listner: BaseListener) {
        this.listener = listner;
    }

    private setSearch(search: Partial<SearchContext>) {
        this.search = {
            ...this.search,
            ...search,
        };

        this.searchDebouncer(() => {
            const filters: Record<string, unknown> = {};
            if (this.search.search) {
                filters.search = this.search.search;
            }
            if (this.search.sortBy && this.search.sortBy !== DEFAULT.sortBy) {
                filters.sortBy = this.search.sortBy;
            }
            if (this.search.shouldSearchArchived !== DEFAULT.shouldSearchArchived) {
                filters.archived = this.search.shouldSearchArchived;
            }
            if (this.search.groupBy !== this.ticketState?.currentDashboard?.settings?.groupBy) {
                filters.groupBy = this.search.groupBy;
            }
            if (this.search.filters) {
                filters.filters = serilizeFilters(this.search.filters);
            }
            if (this.search.isSearching) {
                const currentPath = window.location.pathname.split("/").lastElement;
                const currentChatId = currentPath !== "agent" ? currentPath : undefined;
                this.routing?.historyPushWithSearch(
                    currentChatId ? Paths.AGENT_CHAT : Paths.AGENT,
                    removeEmptyKeys(filters) as {search: string},
                    {
                        chatId: currentChatId as string,
                    },
                );
            }
            this.listener.updateListners();
        });

        this.search.isChanged = this.search.sortBy !== DEFAULT.sortBy
            || this.search.shouldSearchArchived !== DEFAULT.shouldSearchArchived;
    }

    private setSearchAndUpdate(search: Partial<SearchContext>) {
        this.setSearch(search);
        this.listener?.updateListners();
    }

    public showSearch() {
        ShortcutApi.instance.pushContext("search");
        this.setSearchAndUpdate({
            filters: {
                ...this.ticketState?.currentDashboard?.settings?.filters,
            } as DashboardSettingsFilters,
            groupBy: this.ticketState?.currentDashboard?.settings?.groupBy as DashboardGroupBy,
            ...this.lastSearch,
            isSearching: true,
        });
    }

    public hideSearch() {
        ShortcutApi.instance.popContext("search");
        if (this.isSearching) {
            this.lastSearch = this.search;
            this.routing?.historyPushWithSearch(Paths.AGENT, {});
            this.setSearchAndUpdate({ isSearching: false });
        }
        this.ticketState?.tickets.setSearch([]);
    }

    public setSortBy(sortBy: SearchSortBy) {
        this.setSearchAndUpdate({ sortBy });
    }

    public setSearchQuery(search: string) {
        this.setSearch({ search });
        if (!search) {
            this.listener?.updateListners();
        }
    }

    public setShouldSearchArchived(value: boolean) {
        this.setSearchAndUpdate({ shouldSearchArchived: value });
    }

    public get isSearching() {
        return this.search.isSearching;
    }

    public setRemote(props: {
        isLoadingRemote: boolean,
        numberRemoteResults?: number,
        group?: TicketColumnGroup,
        numericSearch?: number
    }) {
        this.setSearchAndUpdate(props);
        this.ticketState?.tickets.setSearch(props.group?.tickets);
    }

    public get searchContext() {
        return this.search;
    }

    public get dashboardSettings() {
        return {
            sortBy: this.search.sortBy ? SORT_TO_DASHBOARD[this.search.sortBy] : DashboardSortBy.Manual,
            filters: this.search.filters,
            groupBy: this.search.groupBy,
        } as DashboardSettings;
    }

    public set dashboardSettings(settings: DashboardSettings) {
        this.setSearchAndUpdate({
            filters: { ...settings.filters },
            groupBy: settings.groupBy ?? DashboardGroupBy.NoGroup,
        });
    }
}
